import React from 'react';
import Skeleton from '@/components/ui/skeleton';

interface AIChatSkeletonProps {
  sidebar?: boolean;
}

export default function AIChatSkeleton({ sidebar = true }: AIChatSkeletonProps) {
  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      {/* 左侧边栏骨架 */}
      {sidebar && (
        <aside className="fixed md:relative z-30 h-full w-80 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 md:translate-x-0">
          <div className="flex flex-col h-full">
            {/* 顶部标题骨架 */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <Skeleton width="100px" height="24px" />
                <div className="flex gap-2">
                  <Skeleton variant="circle" width="32px" height="32px" />
                  <Skeleton variant="circle" width="32px" height="32px" />
                </div>
              </div>
            </div>

            {/* 学生信息卡片骨架 */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* 学生信息骨架 */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Skeleton width="80px" height="20px" />
                  <Skeleton variant="circle" width="100px" height="100px" />
                </div>
                
                <div className="space-y-2">
                  <Skeleton width="120px" height="16px" />
                  <Skeleton width="180px" height="16px" />
                </div>

                {/* 基本信息网格骨架 */}
                <div className="grid grid-cols-2 gap-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="space-y-2">
                      <Skeleton width="60px" height="12px" />
                      <Skeleton width="100%" height="16px" />
                    </div>
                  ))}
                </div>

                {/* 成绩卡片骨架 */}
                <div className="p-3 space-y-3">
                  <Skeleton width="80px" height="16px" />
                  <div className="grid grid-cols-2 gap-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Skeleton key={i} width="100%" height="20px" />
                    ))}
                  </div>
                </div>
              </div>

              {/* 会话列表骨架 */}
              <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="flex justify-between mb-3">
                  <Skeleton width="80px" height="16px" />
                  <Skeleton variant="circle" width="24px" height="24px" />
                </div>
                <div className="space-y-2">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="p-3 space-y-2">
                      <Skeleton width="80%" height="16px" />
                      <Skeleton width="60%" height="12px" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </aside>
      )}

      {/* 主区域骨架 */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* 顶部导航骨架 */}
        <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {sidebar && (
                <Skeleton className="md:hidden" variant="circle" width="36px" height="36px" />
              )}
              <div className="space-y-2">
                <Skeleton width="120px" height="20px" />
                <Skeleton width="80px" height="12px" />
              </div>
            </div>
            <Skeleton variant="circle" width="36px" height="36px" />
          </div>
        </header>

        {/* 聊天内容区域骨架 */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* 欢迎区域骨架 */}
          <div className="max-w-2xl mx-auto pt-8">
            <div className="text-center mb-8">
              <Skeleton width="150px" height="40px" className="mx-auto mb-2" />
              <Skeleton width="80%" height="20px" className="mx-auto" />
            </div>

            {/* 快捷问题骨架 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} width="100%" height="60px" />
              ))}
            </div>

            {/* 功能卡片骨架 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="p-4 space-y-3">
                  <Skeleton width="40px" height="40px" className="mb-2" />
                  <Skeleton width="80%" height="20px" />
                  <Skeleton width="100%" height="12px" />
                  <Skeleton width="100%" height="12px" />
                </div>
              ))}
            </div>
          </div>

          {/* AI响应骨架 */}
          <div className="max-w-3xl mx-auto">
            <div className="flex items-start gap-3 mb-4">
              <Skeleton variant="circle" width="32px" height="32px" />
              <div className="flex-1 space-y-2">
                <Skeleton width="60px" height="16px" />
                <Skeleton width="100%" height="16px" />
                <Skeleton width="90%" height="16px" />
                <Skeleton width="70%" height="16px" />
              </div>
            </div>

            {/* 用户输入骨架 */}
            <div className="flex justify-end">
              <div className="max-w-md w-full space-y-2">
                <Skeleton width="100%" height="16px" />
                <Skeleton width="80%" height="16px" />
              </div>
            </div>
          </div>
        </div>

        {/* 输入区域骨架 */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-4">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-end gap-2">
              <div className="flex-1 space-y-2">
                <Skeleton width="100%" height="48px" />
              </div>
              <div className="flex gap-2">
                <Skeleton variant="circle" width="48px" height="48px" />
                <Skeleton variant="circle" width="48px" height="48px" />
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}